import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

export type ReadmeResponse = {
  content: string;
  sourceUrl?: string;
  path?: string;
};

export type ReadmeRequestOptions = {
  entityRef?: string;
  projectSlug?: string;
  ref?: string;
};

export interface ReadmeApi {
  getReadme(options: ReadmeRequestOptions): Promise<ReadmeResponse>;
}

export const readmeApiRef = createApiRef<ReadmeApi>({
  id: 'plugin.readme.service',
});

class ReadmeClient implements ReadmeApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getReadme({
    entityRef,
    projectSlug,
    ref,
  }: ReadmeRequestOptions): Promise<ReadmeResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('readme');
    const params = new URLSearchParams();

    if (projectSlug) {
      params.set('projectSlug', projectSlug);
    }

    if (entityRef) {
      params.set('entityRef', entityRef);
    }

    if (ref) {
      params.set('ref', ref);
    }

    const response = await this.fetchApi.fetch(
      `${baseUrl}/readme?${params.toString()}`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const data = (await response.json()) as ReadmeResponse;

    if (!data.content) {
      throw new Error('Empty README response received from backend');
    }

    return data;
  }
}

export const readmeApiFactory = createApiFactory({
  api: readmeApiRef,
  deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
  factory: ({ discoveryApi, fetchApi }) =>
    new ReadmeClient({ discoveryApi, fetchApi }),
});
